/**
 * useShopFilterAttributes — CDT-value filter set for the shop list.
 *
 * Refetched whenever the selected category set changes (the storefront
 * endpoint returns CDT values used by products IN those categories
 * only). When no categories are selected the hook returns an empty
 * list — the page hides the CDT panel in that case.
 *
 * Returned flat (one row per (attribute, value)); the page groups by
 * attribute_id to render one checkbox list per attribute.
 */

import { useEffect, useMemo, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES } from '../constants/routes.constants'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export interface ShopAttributeValueDto {
  readonly attribute_id: string
  readonly attribute_label: string
  readonly value_id: string
  readonly value_label: string
}

export interface ShopAttributeGroup {
  readonly attribute_id: string
  readonly attribute_label: string
  readonly values: ReadonlyArray<{ readonly id: string; readonly label: string }>
}

interface Body {
  readonly success: true
  readonly data: { readonly items: ShopAttributeValueDto[] }
}

export interface UseShopFilterAttributesReturn {
  readonly groups: ReadonlyArray<ShopAttributeGroup>
  readonly isLoading: boolean
  readonly error: string | null
}

export function useShopFilterAttributes(
  categoryIds: ReadonlyArray<string>
): UseShopFilterAttributesReturn {
  const [items, setItems] = useState<ShopAttributeValueDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Stable key so the effect doesn't fire on every render when the
  // parent passes a fresh array of the same ids.
  const key = useMemo(() => [...categoryIds].sort().join(','), [categoryIds])

  useEffect(() => {
    if (key.length === 0) {
      setItems([])
      setError(null)
      setIsLoading(false)
      return
    }
    let cancelled = false
    const ac = new AbortController()
    setIsLoading(true)
    setError(null)
    ;(async () => {
      try {
        let res: Response
        try {
          res = await fetch(
            `${APIBASE}${APIROUTES.SHOPFILTERATTRIBUTES}?categoryIds=${encodeURIComponent(key)}`,
            { signal: ac.signal }
          )
        } catch (err) {
          if (cancelled) return
          if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
          throw err
        }
        if (cancelled) return
        if (res.status >= 500 || !res.ok) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
        const body = (await res.json()) as Body
        if (!cancelled) setItems(body.data.items)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : SERVER_UNAVAILABLE_MESSAGE)
          setItems([])
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => { cancelled = true; ac.abort() }
  }, [key])

  // Group rows by attribute_id for the per-attribute checkbox lists.
  const groups = useMemo<ReadonlyArray<ShopAttributeGroup>>(() => {
    const byId = new Map<string, ShopAttributeGroup>()
    for (const row of items) {
      let g = byId.get(row.attribute_id)
      if (g === undefined) {
        g = { attribute_id: row.attribute_id, attribute_label: row.attribute_label, values: [] }
        byId.set(row.attribute_id, g)
      }
      ;(g.values as Array<{ id: string; label: string }>).push({
        id: row.value_id,
        label: row.value_label
      })
    }
    return [...byId.values()]
  }, [items])

  return { groups, isLoading, error }
}
